import { useMemo } from "react"

import useDate from "~/ui-rtk/hooks/date"
import { useCubeQuery } from "@cubejs-client/react"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import { type Query as TCubeQuery } from "@cubejs-client/core"

const dayjs = useDate()

interface CubeRow {
  "company_brand_comparison.company_name": string
  "company_brand_comparison.brand_score": string | number
  "company_brand_comparison.model": string
  "company_brand_comparison.category": string
}

interface UseAIComparisonProps {
  selectedCategory: string
  dateRange?: [Date, Date]
}

interface UseAIComparisonResult {
  models: string[]
  companies: string[]
  categories: string[]
  scoreMap: Record<string, Record<string, number>>
  isLoading: boolean
}

export const useAIComparison = ({
  selectedCategory,
  dateRange,
}: UseAIComparisonProps): UseAIComparisonResult => {
  const currentCompany = useCurrentCompany()

  // Memoize current company name to prevent unnecessary recalculations
  const currentCompanyName = useMemo(
    () => currentCompany?.name || "",
    [currentCompany?.name],
  )

  // Memoize competitors array to prevent recalculation in companies sorting
  const competitors = useMemo(
    () =>
      [
        ...(currentCompany?.metadata?.keywords?.google?.competitors || []),
        currentCompanyName,
      ].filter(Boolean),
    [
      currentCompany?.metadata?.keywords?.google?.competitors,
      currentCompanyName,
    ],
  )

  const baseQuery = useMemo<TCubeQuery>(
    () => ({
      measures: [],
      dimensions: [
        "company_brand_comparison.company_name",
        "company_brand_comparison.model",
        "company_brand_comparison.category",
        "company_brand_comparison.brand_score",
      ],
      timeDimensions: [
        {
          dimension: "company_brand_comparison.md_date",
        },
      ],
      order: {
        "company_brand_comparison.brand_score": "desc" as const,
      },
    }),
    [],
  )

  const cubeQueryConfig = useMemo(
    () =>
      polyfillCubeQueryWithTimeDimension(baseQuery, {
        dateRange: [
          dayjs(dateRange?.[0]).toDate(),
          dayjs(dateRange?.[1]).toDate(),
        ],
      }),
    [baseQuery, dateRange],
  )

  const { resultSet, isLoading } = useCubeQuery(cubeQueryConfig)

  const data = useMemo(() => {
    if (!resultSet) return [] as CubeRow[]
    return resultSet.tablePivot() as unknown as CubeRow[]
  }, [resultSet])

  // Filter data by selected category
  const filteredData = useMemo(() => {
    if (!selectedCategory) return data
    return data.filter(
      row => row["company_brand_comparison.category"] === selectedCategory,
    )
  }, [data, selectedCategory])

  const models = useMemo(
    () =>
      [
        ...new Set(
          filteredData.map(row => row["company_brand_comparison.model"]),
        ),
      ].sort(),
    [filteredData],
  )

  const companies = useMemo(() => {
    const uniqueCompanies = [
      ...new Set(
        filteredData.map(row => row["company_brand_comparison.company_name"]),
      ),
    ]

    // Sort companies: current company first, then real competitors, then AI suggested at the end
    return uniqueCompanies.sort((a, b) => {
      const aLower = a.toLowerCase()
      const bLower = b.toLowerCase()
      const currentCompanyLower = currentCompanyName.toLowerCase()

      // Current company always first
      if (aLower === currentCompanyLower) return -1
      if (bLower === currentCompanyLower) return 1

      // Check if companies are in competitors list (real competitors vs AI suggested)
      const aIsCompetitor = competitors
        .map(c => c.toLowerCase())
        .includes(aLower)
      const bIsCompetitor = competitors
        .map(c => c.toLowerCase())
        .includes(bLower)

      // Both are competitors or both are AI suggested - maintain alphabetical order
      if (aIsCompetitor === bIsCompetitor) {
        return a.localeCompare(b)
      }

      // Real competitors come before AI suggested
      if (aIsCompetitor && !bIsCompetitor) return -1
      if (!aIsCompetitor && bIsCompetitor) return 1

      return 0
    })
  }, [filteredData, currentCompanyName, competitors])

  const categories = useMemo(
    () =>
      [...new Set(data.map(row => row["company_brand_comparison.category"]))]
        .filter(Boolean)
        .sort(),
    [data],
  )

  const scoreMap = useMemo(() => {
    const tempMap: Record<
      string,
      Record<string, { sum: number; count: number }>
    > = {}

    // First pass: sum up scores and count occurrences
    filteredData.forEach(row => {
      const company = row["company_brand_comparison.company_name"]
      const model = row["company_brand_comparison.model"]
      const score = Number(row["company_brand_comparison.brand_score"]) || 0

      // Skip invalid scores
      if (isNaN(score)) {
        return
      }

      if (!tempMap[company]) {
        tempMap[company] = {}
      }
      if (!tempMap[company][model]) {
        tempMap[company][model] = { sum: 0, count: 0 }
      }

      tempMap[company][model].sum += score
      tempMap[company][model].count += 1
    })

    // Second pass: calculate averages
    const finalMap: Record<string, Record<string, number>> = {}
    Object.entries(tempMap).forEach(([company, modelScores]) => {
      finalMap[company] = {}
      Object.entries(modelScores).forEach(([model, { sum, count }]) => {
        finalMap[company][model] = count > 0 ? Number(sum / count) : 0
      })
    })

    return finalMap
  }, [filteredData])

  return {
    models,
    companies,
    categories,
    scoreMap,
    isLoading,
  }
}

export default useAIComparison
